<mat-card style="width: min-content; max-width: 100%;">
    <mat-card-header>
        <div class="g-just-flex flex-between w-100 flex-wrap gap-20">
            <div class="left">
                <mat-card-title>Invoices</mat-card-title>
                <mat-card-subtitle>
                    View invoices and their related charge items (dispense, service request, etc).
                </mat-card-subtitle>
            </div>

            <div class="right gap-10 g-just-flex"
                style="flex: 1 1 auto; justify-content: flex-end; align-items: center;">
                <app-patient-filter></app-patient-filter>
                @if (canAdd$ | async) {
                <a mat-flat-button>
                    <mat-icon>add</mat-icon>
                    Add
                </a>
                }
            </div>
        </div>
    </mat-card-header>

    <mat-card-content class="my-12">
        <mat-divider></mat-divider>

        <!-- Date Period Filter Bar -->
        <div class="period-filter-bar g-just-flex" style="flex-wrap: wrap; gap: 16px; margin: 16px 0; align-items: center; justify-content: space-between;">
            <div class="period-presets g-just-flex gap-10" style="flex-wrap: wrap; align-items: center;">
                <mat-icon class="period-info-icon" style="color: var(--mat-sys-primary);">date_range</mat-icon>
                <span class="date-source-hint text-muted" style="margin-right: 12px; font-size: 14px;">
                    Filtered by invoice date
                </span>
                @for (preset of periodPresets; track preset.id) {
                    @if (preset.id === activePeriodPreset) {
                    <button mat-flat-button
                        color="primary"
                        (click)="onPeriodChanged(preset.id)"
                        style="border-radius: 20px;">
                        {{ preset.label }}
                    </button>
                    } @else {
                    <button mat-stroked-button
                        (click)="onPeriodChanged(preset.id)"
                        style="border-radius: 20px;">
                        {{ preset.label }}
                    </button>
                    }
                }
                @if (activePeriodPreset === 'custom') {
                <button mat-flat-button
                    color="primary"
                    (click)="toggleCustomRange()"
                    style="border-radius: 20px;">
                    Custom
                </button>
                } @else {
                <button mat-stroked-button
                    (click)="toggleCustomRange()"
                    style="border-radius: 20px;">
                    Custom
                </button>
                }
            </div>
            
            <div class="current-period-display text-muted" style="font-size: 14px; font-weight: 500;">
                {{ getFormattedPeriodRange() }}
            </div>
        </div>

        @if (showCustomRange) {
        <div class="custom-range-panel g-just-flex" style="padding: 16px; background-color: var(--mat-sys-surface-variant); border-radius: 8px; margin-bottom: 16px; gap: 16px; align-items: center;">
            <mat-form-field appearance="outline" class="date-field" subscriptSizing="dynamic">
                <mat-label>Start Date</mat-label>
                <input matInput [matDatepicker]="startPicker" [(ngModel)]="customStartDate">
                <mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
                <mat-datepicker #startPicker></mat-datepicker>
            </mat-form-field>

            <span style="color: var(--mat-sys-on-surface-variant);">to</span>

            <mat-form-field appearance="outline" class="date-field" subscriptSizing="dynamic">
                <mat-label>End Date</mat-label>
                <input matInput [matDatepicker]="endPicker" [(ngModel)]="customEndDate">
                <mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
                <mat-datepicker #endPicker></mat-datepicker>
            </mat-form-field>

            <button mat-flat-button color="primary" (click)="onCustomDateApplied()" [disabled]="!customStartDate || !customEndDate">
                Apply Custom Range
            </button>
            <button mat-icon-button (click)="toggleCustomRange()">
                <mat-icon>close</mat-icon>
            </button>
        </div>
        }
        <mat-divider></mat-divider>
        <!-- Invoice Type Tabs -->
        <div class="my-15">
            <app-custom-tabs [tabs]="invoiceTypeTabs" [activeTabId]="invoiceTypeTab"
                (tabClicked)="applyInvoiceTypeTab($event)"></app-custom-tabs>
        </div>

        <div class="table-or-empty" style="background-color: var(--mat-sys-surface)">
            @if (tableDataSource?.data?.length) {
            <table style="min-width: 860px; width: unset" mat-table matSort [dataSource]="tableDataSource.data"
                class="mat-elevation-z8 demo-table">

                <!-- INVOICE ID COLUMN -->
                <ng-container matColumnDef="invoiceId">
                    <th mat-header-cell *matHeaderCellDef>Invoice ID</th>
                    <td mat-cell *matCellDef="let element">
                        <span>{{ element['Invoice_Identifier'] || 'N/A' }}</span>
                    </td>
                </ng-container>

                <!-- ENCOUNTER GROUP COLUMN -->
                <ng-container matColumnDef="encounterGroup">
                    <th mat-header-cell *matHeaderCellDef>Encounter</th>

                    <td mat-cell *matCellDef="let element" style="min-width: 150px;">
                        @if(element['_encounterGroupKeyString'] && element['_encounterGroupKeyString'] !== 'N/A') {
                        <span class="group-badge"
                            matTooltip="Part of a group of {{ element['_groupCount'] }} invoices">
                            <mat-icon style="font-size:13px;width:13px;height:13px;">folder_open</mat-icon>
                            <span [appReferenceDisplay]="element['Encounter']" InstantfetchAndRepace="true">
                                {{element['Encounter']}}
                            </span>
                            <span class="group-count">{{ element['_groupCount'] }}</span>
                        </span>
                        } @else {
                        <span class="text-muted">No Encounter</span>
                        }
                    </td>
                </ng-container>

                <!-- DATE -->
                <ng-container matColumnDef="date">
                    <th mat-header-cell *matHeaderCellDef>Date</th>
                    <td mat-cell *matCellDef="let row; let i = index" style="padding-top: 4px; padding-bottom: 4px;">
                        <span class="restrict-length">{{ row['Day'] || row['Date'] || 'N/A' }}</span>
                        <br>
                        <span class="restrict-length subtitle-text-color-2 small-subtitle">{{ row['Time'] || ''
                            }}</span>
                    </td>
                </ng-container>

                <!-- STATUS -->
                <ng-container matColumnDef="status">
                    <th mat-header-cell *matHeaderCellDef>Status</th>
                    <td mat-cell *matCellDef="let row">
                        <div [appChips]="row.status" fontSize="14px"></div>
                    </td>
                </ng-container>

                <!-- SUBJECT -->
                <ng-container matColumnDef="subject">
                    <th mat-header-cell *matHeaderCellDef>Subject</th>
                    <td mat-cell *matCellDef="let row">
                        <span [appReferenceDisplay]="row.subject?.reference || row['Subject']"
                            InstantfetchAndRepace="true"></span>
                    </td>
                </ng-container>

                <!-- TYPE -->
                <ng-container matColumnDef="type">
                    <th mat-header-cell *matHeaderCellDef>Type</th>
                    <td mat-cell *matCellDef="let row">
                        {{ row['Invoice Type'] || 'N/A' }}
                    </td>
                </ng-container>

                <!-- TOTAL -->
                <ng-container matColumnDef="total">
                    <th mat-header-cell *matHeaderCellDef>Total</th>
                    <td mat-cell *matCellDef="let row">
                        <strong>{{ row['Total'] || 'N/A' }}</strong>
                    </td>
                </ng-container>

                <!-- ACTION -->
                <ng-container matColumnDef="view">
                    <th mat-header-cell *matHeaderCellDef>Action</th>
                    <td mat-cell *matCellDef="let row; let i = index">
                        <button mat-icon-button [matMenuTriggerFor]="actionMenu" (click)="$event.stopPropagation()">
                            <mat-icon>more_vert</mat-icon>
                        </button>
                        <mat-menu #actionMenu="matMenu">
                            <button mat-menu-item (click)="showRow(row)">
                                <mat-icon>visibility</mat-icon>
                                <span>View Invoice</span>
                            </button>
                            @if(row['_encounterGroupKeyString'] && row['_encounterGroupKeyString'] !== 'N/A') {
                            <a mat-menu-item
                                [routerLink]="['/app/patient-bills', row['_encounterGroupKeyString'].split('/')[row['_encounterGroupKeyString'].split('/').length - 1], row['subject']?.reference?.split('/')[row['subject']?.reference?.split('/').length - 1]]"
                                [queryParams]="{ type: invoiceTypeTab }">
                                <mat-icon>list</mat-icon>
                                <span>Group View</span>
                            </a>
                            }
                        </mat-menu>
                    </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="displayedColumns" matHeaderRowDefSticky="true"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns;" style="cursor:pointer;"></tr>
            </table>
            } @else {
            <div style="padding: 48px; text-align: center;">
                <app-empty-state 
                    [subtitle]="(!effectivePatientId && activePeriodPreset === 'all') ? 'Please select a Patient OR choose a Date Period (e.g. 30 Days) to view invoices. Fetching all invoices across all time is not permitted.' : 'No invoices found matching the current filters.'"
                    title="No Invoices"
                    icon="receipt_long">
                </app-empty-state>
            </div>
            }
        </div>
    </mat-card-content>
</mat-card>